import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Dropdown, Select, Space, Table, Tag, Typography, Pagination } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
    clearApiAccessCreateUserStatus,
    clearApiAccessUpdatePasswordStatus,
    clearApiAccessUpdateUserStatus,
    createApiAccessUserRequest,
    getApiAccessRolesRequest,
    getApiAccessUsersRequest,
    updateApiAccessPasswordRequest,
    updateApiAccessUserRequest,
} from 'redux/actions/configProvider/api-access-actions';
import {
    getApiAccessCreateUserLoading,
    getApiAccessCreateUserResult,
    getApiAccessCreateUserSuccess,
    getApiAccessRoles,
    getApiAccessTotalRecords,
    getApiAccessUpdatePasswordLoading,
    getApiAccessUpdatePasswordResult,
    getApiAccessUpdatePasswordSuccess,
    getApiAccessUpdateUserLoading,
    getApiAccessUpdateUserSuccess,
    getApiAccessUsers,
    getApiAccessUsersLoading,
} from 'redux/selectors/configProvider/api-access-selectors';
import type { MenuProps } from 'antd';
import { CloseCircleOutlined, DownOutlined, PlusCircleOutlined } from '@ant-design/icons';
import Search from 'antd/es/input/Search';
import ApiAccessGeneratedPasswordModal from './ApiAccessGeneratedPasswordModal';
import ApiAccessPasswordModal from './ApiAccessPasswordModal';
import ApiAccessUserModal, { IApiAccessUserFormValues } from './ApiAccessUserModal';

interface IApiAccessUser {
    id: number;
    name?: string;
    username?: string;
    principalType?: string;
    blocked?: boolean;
    role?: string;
    roles?: Array<string | { id?: number; name?: string }>;
    authorities?: string[];
}

interface IApiAccessUserRow extends IApiAccessUser {
    key: number;
    displayName: string;
}

interface IApiAccessRole {
    id?: number;
    name?: string;
    description?: string;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SORT_BY = 'id';
const TABLE_HEADER_CELL_CLASS =
    '!bg-white !px-4 !py-3 !text-sm !font-semibold !text-gray-900 !border-b !border-gray-100';
const TABLE_BODY_CELL_CLASS = '!px-4 !py-3.5 !align-middle !border-b !border-gray-50';

const formatLabel = (value?: unknown) => {
    if (typeof value !== 'string' || !value) {
        return '-';
    }

    return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeRoleValue = (role: string | { id?: number; name?: string } | undefined) => {
    if (typeof role === 'string' && role) {
        return role;
    }

    if (role && typeof role !== 'string' && typeof role.name === 'string' && role.name) {
        return role.name;
    }

    return null;
};

const getUserRoles = (user: IApiAccessUser) => {
    if (user.roles && user.roles.length > 0) {
        return user.roles
            .map((role) => normalizeRoleValue(role))
            .filter((role): role is string => Boolean(role));
    }

    if (typeof user.role === 'string' && user.role) {
        return [user.role];
    }

    return (user.authorities || []).filter(
        (role): role is string => typeof role === 'string' && Boolean(role),
    );
};

const ApiAccessList: React.FC = () => {
    const dispatch = useDispatch();
    const users = useSelector((state: any) => getApiAccessUsers(state));
    const totalRecords = useSelector((state: any) => getApiAccessTotalRecords(state));
    const loading = useSelector((state: any) => getApiAccessUsersLoading(state));
    const roles = useSelector((state: any) => getApiAccessRoles(state));
    const createUserLoading = useSelector((state: any) => getApiAccessCreateUserLoading(state));
    const createUserSuccess = useSelector((state: any) => getApiAccessCreateUserSuccess(state));
    const createUserResult = useSelector((state: any) => getApiAccessCreateUserResult(state));
    const updateUserLoading = useSelector((state: any) => getApiAccessUpdateUserLoading(state));
    const updateUserSuccess = useSelector((state: any) => getApiAccessUpdateUserSuccess(state));
    const updatePasswordLoading = useSelector((state: any) => getApiAccessUpdatePasswordLoading(state));
    const updatePasswordSuccess = useSelector((state: any) => getApiAccessUpdatePasswordSuccess(state));
    const updatePasswordResult = useSelector((state: any) => getApiAccessUpdatePasswordResult(state));
    const [searchInput, setSearchInput] = React.useState('');
    const [searchValue, setSearchValue] = React.useState('');
    const [statusFilter, setStatusFilter] = React.useState<string>();
    const [principalTypeFilter, setPrincipalTypeFilter] = React.useState<string>();
    const [roleFilter, setRoleFilter] = React.useState<string>();
    const [isCreateModalOpen, setIsCreateModalOpen] = React.useState(false);
    const [isGeneratedPasswordModalOpen, setIsGeneratedPasswordModalOpen] = React.useState(false);
    const [editingUser, setEditingUser] = React.useState<IApiAccessUser | null>(null);
    const [passwordUser, setPasswordUser] = React.useState<IApiAccessUser | null>(null);
    const [generatedPasswordUsername, setGeneratedPasswordUsername] = React.useState<string>();
    const [generatedPasswordContext, setGeneratedPasswordContext] = React.useState<'create' | 'reset'>('create');
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);
    const [tableHeight, setTableHeight] = React.useState(0);

    const calculateTableHeight = React.useCallback(() => {
        const height = window.innerHeight * 0.8 - 194;
        setTableHeight(height);
    }, []);

    React.useEffect(() => {
        calculateTableHeight();
        window.addEventListener('resize', calculateTableHeight);

        return () => window.removeEventListener('resize', calculateTableHeight);
    }, [calculateTableHeight]);

    const fetchUsers = React.useCallback(() => {
        dispatch(getApiAccessUsersRequest({
            username: searchValue || undefined,
            blocked:
                statusFilter === 'Blocked' ? true : statusFilter === 'Active' ? false : undefined,
            principalType: principalTypeFilter || undefined,
            role: roleFilter || undefined,
            paging: { page, pageSize, sortBy: DEFAULT_SORT_BY },
        }));
    }, [dispatch, page, pageSize, principalTypeFilter, roleFilter, searchValue, statusFilter]);

    React.useEffect(() => {
        dispatch(getApiAccessRolesRequest());
    }, [dispatch]);

    React.useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    React.useEffect(() => {
        if (createUserSuccess) {
            setIsCreateModalOpen(false);
            setGeneratedPasswordContext('create');
            setGeneratedPasswordUsername(createUserResult?.username);
            setIsGeneratedPasswordModalOpen(true);
            fetchUsers();
        }
    }, [createUserResult?.username, createUserSuccess, dispatch, fetchUsers]);

    React.useEffect(() => {
        if (updateUserSuccess) {
            setEditingUser(null);
            fetchUsers();
            dispatch(clearApiAccessUpdateUserStatus());
        }
    }, [dispatch, fetchUsers, updateUserSuccess]);

    React.useEffect(() => {
        if (updatePasswordSuccess) {
            setGeneratedPasswordUsername(passwordUser?.username || passwordUser?.name);
            setPasswordUser(null);
            setGeneratedPasswordContext('reset');
            setIsGeneratedPasswordModalOpen(true);
        }
    }, [passwordUser?.name, passwordUser?.username, updatePasswordSuccess]);

    const handleSearch = (value?: string) => {
        const nextValue = typeof value === 'string' ? value : searchInput;
        setSearchValue(nextValue.trim());
        setPage(1);
    };

    const handlePageChange = (nextPage: number, nextPageSize: number) => {
        setPage(nextPage);
        setPageSize(nextPageSize);
    };

    const handleCreateUser = React.useCallback((values: IApiAccessUserFormValues) => {
        dispatch(createApiAccessUserRequest({
            username: values.username.trim(),
            principalType: values.principalType,
            blocked: !values.enabled,
            roles: values.roles,
        }));
    }, [dispatch]);

    const handleUpdateUser = React.useCallback((values: IApiAccessUserFormValues) => {
        if (!editingUser?.id) {
            return;
        }

        dispatch(updateApiAccessUserRequest({
            id: editingUser.id,
            username: values.username.trim(),
            blocked: !values.enabled,
            roles: values.roles,
        }));
    }, [dispatch, editingUser]);

    const handleUpdatePassword = React.useCallback(() => {
        if (!passwordUser?.id) {
            return;
        }

        dispatch(updateApiAccessPasswordRequest({
            id: passwordUser.id,
        }));
    }, [dispatch, passwordUser]);

    const handleToggleStatus = React.useCallback((user: IApiAccessUser) => {
        if (!user.id) {
            return;
        }

        dispatch(updateApiAccessUserRequest({
            id: user.id,
            username: (user.username || user.name || `${user.id}`).trim(),
            blocked: !user.blocked,
            roles: getUserRoles(user),
        }));
    }, [dispatch]);

    const dataSource = React.useMemo<IApiAccessUserRow[]>(() =>
        (users || []).map((user: IApiAccessUser) => ({
            ...user,
            key: user.id,
            displayName: user.name || user.username || `${user.id}`,
        })), [users]);

    const statusOptions = React.useMemo(
        () => [
            { label: 'Active', value: 'Active' },
            { label: 'Blocked', value: 'Blocked' },
        ],
        [],
    );

    const principalTypeOptions = React.useMemo(() => {
        const uniqueTypes = new Set<string>();
        (users || []).forEach((user: IApiAccessUser) => {
            if (user.principalType) {
                uniqueTypes.add(user.principalType);
            }
        });

        return Array.from(uniqueTypes).map((type) => ({ label: formatLabel(type), value: type }));
    }, [users]);

    const roleOptions = React.useMemo(
        () =>
            (roles || []).map((role: IApiAccessRole) => ({
                label: formatLabel(role.name),
                value: role.name || '',
            })).filter((role: { label: string; value: string }) => Boolean(role.value)),
        [roles],
    );

    const getActionsMenuItems = React.useCallback(
        (record: IApiAccessUser): MenuProps['items'] => [
            {
                key: 'edit',
                onClick: () => setEditingUser(record),
                label: 'Edit',
            },
            {
                key: 'reset-password',
                onClick: () => setPasswordUser(record),
                label: 'Reset Password',
            },
            {
                key: 'toggle-status',
                onClick: () => handleToggleStatus(record),
                label: record.blocked ? 'Enable' : 'Disable',
            },
        ],
        [handleToggleStatus],
    );

    const columns: ColumnsType<IApiAccessUser> = [
        {
            title: 'ID',
            dataIndex: 'id',
            width: 120,
            onHeaderCell: () => ({ className: TABLE_HEADER_CELL_CLASS }),
            onCell: () => ({ className: TABLE_BODY_CELL_CLASS }),
        },
        {
            title: 'Name',
            dataIndex: 'displayName',
            onHeaderCell: () => ({ className: TABLE_HEADER_CELL_CLASS }),
            onCell: () => ({ className: TABLE_BODY_CELL_CLASS }),
            render: (_value, record) => (
                <Link
                    to={`/config-provider/api-access/${record.id}`}
                    className="text-[#df1f26] underline transition-colors hover:text-[#c91b21]"
                >
                    {record.name || record.username || record.id}
                </Link>
            ),
        },
        {
            title: 'Principal type',
            dataIndex: 'principalType',
            onHeaderCell: () => ({ className: TABLE_HEADER_CELL_CLASS }),
            onCell: () => ({ className: TABLE_BODY_CELL_CLASS }),
            render: (value) => formatLabel(value),
        },
        {
            title: 'Role',
            key: 'roles',
            width: 220,
            onHeaderCell: () => ({ className: TABLE_HEADER_CELL_CLASS }),
            onCell: () => ({ className: TABLE_BODY_CELL_CLASS }),
            render: (_value, record) => {
                const roles = getUserRoles(record);

                if (!roles.length) {
                    return '-';
                }

                return roles.map((role) => formatLabel(role)).join(', ');
            },
        },
        {
            title: 'Status',
            dataIndex: 'blocked',
            onHeaderCell: () => ({ className: TABLE_HEADER_CELL_CLASS }),
            onCell: () => ({ className: TABLE_BODY_CELL_CLASS }),
            render: (blocked?: boolean) => (
                <Tag
                    className={`!mr-0 !rounded !px-2.5 !py-0.5 !text-sm ${
                        blocked
                            ? '!border-[#ffccc7] !bg-[#fff2f0] !text-[#ff4d4f]'
                            : '!border-[#a0d911] !bg-[#f6ffed] !text-[#52c41a]'
                    }`}
                >
                    {blocked ? 'Blocked' : 'Active'}
                </Tag>
            ),
        },
        {
            title: 'Action',
            key: 'action',
            align: 'right',
            width: 180,
            onHeaderCell: () => ({ className: `${TABLE_HEADER_CELL_CLASS} !text-right` }),
            onCell: () => ({ className: `${TABLE_BODY_CELL_CLASS} !text-right` }),
            render: (_value, record) => (
                <Dropdown menu={{ items: getActionsMenuItems(record) }} trigger={['click']} placement="bottomRight">
                    <a>
                        More <DownOutlined />
                    </a>
                </Dropdown>
            ),
        },
    ];
    const totalWidth: number = columns.reduce(
        (total: number, col: any) => total + (col.width || 0),
        0,
    );

    return (
        <div className="min-h-0 h-[calc(100vh-64px)] overflow-hidden bg-[#fafbfc] flex flex-col pb-4">
            <div className="px-6 pt-4 pb-4">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="mb-1 text-xs text-gray-400">Configuration Provider</div>
                        <Typography.Title level={2} className="!m-0 !text-[28px] !font-semibold !text-gray-900">
                            API Access
                        </Typography.Title>
                    </div>
                    <Button
                        type="primary"
                        shape="round"
                        icon={<PlusCircleOutlined />}
                        className="!h-[42px] !border-0 !bg-[#df1f26] !px-[18px] !text-[15px] !shadow-none hover:!bg-[#c91b21] focus:!bg-[#c91b21]"
                        onClick={() => setIsCreateModalOpen(true)}
                    >
                        Create API User
                    </Button>
                </div>
            </div>

            <div className="flex-1 overflow-auto px-6">
                <div className="flex mb-4">
                    <Space.Compact className="flex items-center mr-3">
                        <Select
                            className="w-[150px]"
                            allowClear
                            placeholder="Status"
                            value={statusFilter}
                            onChange={(value) => {
                                setStatusFilter(value);
                                setPage(1);
                            }}
                            options={statusOptions}
                        />
                    </Space.Compact>
                    <Space.Compact className="flex items-center mr-3">
                        <Select
                            className="w-[150px]"
                            allowClear
                            placeholder="Principal type"
                            value={principalTypeFilter}
                            onChange={(value) => {
                                setPrincipalTypeFilter(value);
                                setPage(1);
                            }}
                            options={principalTypeOptions}
                        />
                    </Space.Compact>
                    <Space.Compact className="flex items-center mr-3">
                        <Select
                            className="w-[150px]"
                            allowClear
                            placeholder="Role"
                            value={roleFilter}
                            onChange={(value) => {
                                setRoleFilter(value);
                                setPage(1);
                            }}
                            options={roleOptions}
                        />
                    </Space.Compact>
                    <Space.Compact className="flex items-center w-full">
                        <Search
                            className="flex-1"
                            placeholder="Search"
                            value={searchInput}
                            onChange={(event) => {
                                const nextValue = event.target.value;
                                setSearchInput(nextValue);
                                if (!nextValue) {
                                    handleSearch('');
                                }
                            }}
                            onSearch={handleSearch}
                            suffix={
                                <CloseCircleOutlined
                                    className={`cursor-pointer text-gray-400 hover:text-gray-600 ${searchInput.length > 0 ? 'opacity-100' : 'opacity-0'}`}
                                    onClick={() => {
                                        setSearchInput('');
                                        handleSearch('');
                                    }}
                                />
                            }
                        />
                    </Space.Compact>
                </div>

                <Table
                    dataSource={dataSource}
                    size={"small"}
                    columns={columns}
                    loading={loading}
                    rowKey={(record) => `${record.id}`}
                    className="w-full pb-4 rounded-lg border border-gray-200 bg-white"
                    scroll={{ x: totalWidth, y: tableHeight }}
                    pagination={false}
                />

                <div className="flex justify-end mt-4">
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={totalRecords}
                        onChange={handlePageChange}
                        showSizeChanger
                        pageSizeOptions={['10', '20', '50', '100']}
                        showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                    />
                </div>
            </div>

            <ApiAccessUserModal
                mode="create"
                open={isCreateModalOpen}
                loading={createUserLoading}
                roleOptions={roleOptions}
                onCancel={() => {
                    setIsCreateModalOpen(false);
                    dispatch(clearApiAccessCreateUserStatus());
                }}
                onSubmit={handleCreateUser}
            />

            <ApiAccessUserModal
                mode="edit"
                open={Boolean(editingUser)}
                loading={updateUserLoading}
                roleOptions={roleOptions}
                initialValues={
                    editingUser
                        ? {
                              username: editingUser.username || editingUser.name || '',
                              enabled: !editingUser.blocked,
                              principalType: editingUser.principalType || 'THIRD_PARTY',
                              roles: getUserRoles(editingUser),
                          }
                        : undefined
                }
                onCancel={() => {
                    setEditingUser(null);
                    dispatch(clearApiAccessUpdateUserStatus());
                }}
                onSubmit={handleUpdateUser}
            />

            <ApiAccessPasswordModal
                open={Boolean(passwordUser)}
                loading={updatePasswordLoading}
                username={passwordUser?.username || passwordUser?.name}
                onCancel={() => {
                    setPasswordUser(null);
                    dispatch(clearApiAccessUpdatePasswordStatus());
                }}
                onSubmit={handleUpdatePassword}
            />

            <ApiAccessGeneratedPasswordModal
                open={isGeneratedPasswordModalOpen}
                password={
                    generatedPasswordContext === 'create'
                        ? createUserResult?.rawPassword
                        : updatePasswordResult?.rawPassword
                }
                username={
                    generatedPasswordContext === 'create'
                        ? createUserResult?.username || generatedPasswordUsername
                        : generatedPasswordUsername
                }
                title={generatedPasswordContext === 'create' ? 'API User Created' : 'New Password Generated'}
                description={
                    generatedPasswordContext === 'create'
                        ? 'Copy the password now, because it will not be shown again.'
                        : 'Copy the new password now, because it will not be shown again.'
                }
                onCancel={() => {
                    setIsGeneratedPasswordModalOpen(false);
                    if (generatedPasswordContext === 'create') {
                        dispatch(clearApiAccessCreateUserStatus());
                    } else {
                        dispatch(clearApiAccessUpdatePasswordStatus());
                    }
                    setGeneratedPasswordUsername(undefined);
                }}
            />
        </div>
    );
};

export default ApiAccessList;
